import { SellBanner } from "@/components/shop/SellBanner";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { ACCESSORY_TYPES, ACCESSORY_LABEL, CATEGORIES, CATEGORY_LABEL, CONDITIONS, GAMES, LANGUAGES, RARITIES, SORTS, FINISHES } from "@/lib/constants";
import { ProductCard } from "@/components/shop/ProductCard";
import { Loader } from "@/components/shop/ProtectedRoute";
import { Pager, totalOf } from "@/components/shop/Pager";

const KEYS = ["category", "subcategory", "game", "condition", "language", "rarity", "finish"];

const Group = ({ title, name, options, params, toggle }) => {
  const active = (params.get(name) || "").split(",").filter(Boolean);
  return (
    <div className="border-b border-black/[0.06] py-5">
      <p className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-stone-700">{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const v = o.value ?? o;
          const on = active.includes(v);
          return (
            <button key={v} onClick={() => toggle(name, v)} data-testid={`filter-${name}-${v}`}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-700" : "border-black/10 text-stone-600 hover:border-black/25 hover:text-stone-800"}`}>
              {o.label ?? o}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Filters = ({ params, setParams }) => {
  const [min, setMin] = useState(params.get("min_price") || "");
  const [max, setMax] = useState(params.get("max_price") || "");
  useEffect(() => {
    setMin(params.get("min_price") || "");
    setMax(params.get("max_price") || "");
  }, [params]);
  const toggle = (name, v) => {
    const next = new URLSearchParams(params);
    const cur = (next.get(name) || "").split(",").filter(Boolean);
    const upd = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    upd.length ? next.set(name, upd.join(",")) : next.delete(name);
    if (name === "category" && upd.join(",") !== "accessori") next.delete("subcategory");
    next.delete("page");
    setParams(next);
  };
  const applyPrice = () => {
    const next = new URLSearchParams(params);
    next.delete("page");
    min ? next.set("min_price", min) : next.delete("min_price");
    max ? next.set("max_price", max) : next.delete("max_price");
    setParams(next);
  };
  return (
    <div>
      <Group title="Categoria" name="category" options={CATEGORIES} params={params} toggle={toggle} />
      {params.get("category") === "accessori" && <Group title="Tipo di accessorio" name="subcategory" options={ACCESSORY_TYPES} params={params} toggle={toggle} />}
      <Group title="Gioco" name="game" options={GAMES} params={params} toggle={toggle} />
      <Group title="Condizione" name="condition" options={CONDITIONS} params={params} toggle={toggle} />
      <Group title="Rarità" name="rarity" options={RARITIES} params={params} toggle={toggle} />
      <Group title="Finitura" name="finish" options={FINISHES} params={params} toggle={toggle} />
      <Group title="Lingua" name="language" options={LANGUAGES} params={params} toggle={toggle} />
      <div className="py-5">
        <p className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-stone-700">Prezzo (€)</p>
        <div className="flex items-center gap-2">
          <input type="number" min="0" placeholder="Min" value={min} onChange={(e) => setMin(e.target.value)} data-testid="filter-min-price" className="h-10 w-full rounded-lg border border-black/10 bg-[#FFFBF3] px-3 text-sm outline-none focus:border-amber-500/50" />
          <input type="number" min="0" placeholder="Max" value={max} onChange={(e) => setMax(e.target.value)} data-testid="filter-max-price" className="h-10 w-full rounded-lg border border-black/10 bg-[#FFFBF3] px-3 text-sm outline-none focus:border-amber-500/50" />
          <button onClick={applyPrice} data-testid="filter-price-apply" className="h-10 rounded-lg bg-black/10 px-3 text-sm hover:bg-black/20">OK</button>
        </div>
      </div>
    </div>
  );
};

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const sort = params.get("sort") || "recenti";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: { ...Object.fromEntries(params.entries()), limit: 24, ...(params.get("category") === "singole" ? { in_stock: true } : {}) } })
      .then((r) => { setProducts(r.data); setTotal(totalOf(r)); });
  }, [params]);
  const setPage = (p) => { const n = new URLSearchParams(params); p > 1 ? n.set("page", p) : n.delete("page"); setParams(n); };

  const q = params.get("q");
  const cats = (params.get("category") || "").split(",").filter(Boolean);
  const subs = (params.get("subcategory") || "").split(",").filter(Boolean);
  const title = q ? `Risultati per “${q}”` : cats.length === 1 && subs.length === 1 ? ACCESSORY_LABEL[subs[0]] : cats.length === 1 ? CATEGORY_LABEL[cats[0]] : "Catalogo completo";
  const activeCount = KEYS.reduce((n, k) => n + (params.get(k) ? params.get(k).split(",").length : 0), 0) + (params.get("min_price") || params.get("max_price") ? 1 : 0);
  const setSort = (v) => { const n = new URLSearchParams(params); n.set("sort", v); n.delete("page"); setParams(n); };

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600">Catalogo</p>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="catalog-title">{title}</h1>
          <p className="mt-1 text-sm text-stone-600" data-testid="catalog-count">{products ? `${total} prodotti` : "Caricamento..."}</p>
        </div>
        <div className="flex items-center gap-2">
          {(activeCount > 0 || q) && (
            <button onClick={() => setParams({})} data-testid="filter-clear" className="flex items-center gap-1 rounded-full border border-black/10 px-3 py-2 text-xs text-stone-700 hover:border-rose-400/50 hover:text-rose-600"><X className="h-3 w-3" />Azzera filtri</button>
          )}
          <Sheet>
            <SheetTrigger className="flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm lg:hidden" data-testid="mobile-filters-button"><SlidersHorizontal className="h-4 w-4" />Filtri{activeCount ? ` (${activeCount})` : ""}</SheetTrigger>
            <SheetContent side="left" className="overflow-y-auto border-black/10 bg-[#FFF8EC]">
              <SheetHeader><SheetTitle className="font-display text-stone-900">Filtri</SheetTitle></SheetHeader>
              <Filters params={params} setParams={setParams} />
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] rounded-full border-black/10 bg-[#FFFFFF]" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="border-black/10 bg-[#FFFFFF]">
              {SORTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-8 grid gap-10 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block" data-testid="filters-sidebar"><div className="sticky top-28"><Filters params={params} setParams={setParams} /></div></aside>
        <div>
          {!products ? <Loader /> : products.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/10 p-16 text-center text-stone-600" data-testid="catalog-empty">Nessun prodotto trovato con questi filtri.</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 xl:grid-cols-4" data-testid="catalog-grid">
              {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
            </div>
          )}
          {products && <Pager page={page} total={total} size={24} onPage={setPage} testid="catalog-pager" />}
        </div>
      </div>
      <SellBanner testid="catalog-sell-banner" className="mt-16 !px-0" />
    </div>
  );
}
